import React, { useContext, useEffect, useState } from "react";
import dayjs from "dayjs";
import { LoadingIcon, TopDropdown } from "../.././../components";
import { getGreenhouses } from "../../../utils/api";
import { UserContext } from "../../../context/UserContext";
import { useLocalStorage } from "../../../hooks";

const Dropdowns = ({
  siteMetadata,
  setSiteMetadata,
  currentOrg,
  setCurrentOrg,
  orgData,
  setOrgData,
  orgLoading,
  setOrgLoading,
  currentSite,
  setCurrentSite,
  siteData,
  setSiteData,
  siteLoading,
  setSiteLoading,
  currentPlot,
  setCurrentPlot,
  currentCrop,
  setCurrentCrop,
  cropData,
  setCropData,
  cropLoading,
  setCropLoading,
  currentGreenhouse,
  setCurrentGreenhouse,
  currentDate,
  setCurrentDate,
  plotData,
  setPlotData,
  greenhouseData,
  setGreenhouseData,
  setDropdownLoading,
  settingDefaults,
  setSettingDefaults,
  searchParams,
  setSearchParams,
  dateData,
  setDateData,
}) => {
  const [defaultDropdown, setDefaultDropdown] = useLocalStorage(
    "dropdowns",
    null,
  );
  const { userData } = useContext(UserContext);

  const [greenHouseLabel, setGreenHouseLabel] = useState("Greenhouse");
  const [blockLabel, setBlockLabel] = useState("Block");
  const [plotLoading, setPlotLoading] = useState(false);
  const [greenhouseLoading, setGreenhouseLoading] = useState(false);

  const getPlotDates = (plot) => (plot?.harvests || []).map((dateObj) => dateObj.date);

  const findPlot = (plotId, greenhouse = currentGreenhouse) =>
    (greenhouse?.plots || plotData || []).find(
      (plot) => plot.id === plotId && (!plot.greenhouse_id || plot.greenhouse_id === greenhouse?.id),
    ) ||
    (greenhouse?.plots || plotData || []).find((plot) => plot.id === plotId);

  const syncPlotSelection = (plot, greenhouse = currentGreenhouse, preferredDate = null) => {
    if (!plot) return;
    const dates = getPlotDates(plot);
    const nextDate = preferredDate && dates.includes(preferredDate) ? preferredDate : dates[0] || null;

    setCurrentPlot(plot);
    setDateData(dates);
    setCurrentDate(nextDate);

    const params = new URLSearchParams(searchParams);
    if (greenhouse) params.set("greenhouse", greenhouse.id);
    params.set("plot", plot.id);
    if (nextDate) params.set("date", nextDate);
    else params.delete("date");
    setSearchParams(params);

    const orgObj = defaultDropdown?.[currentOrg] || {};
    setDefaultDropdown({
      ...defaultDropdown,
      [currentOrg]: {
        ...orgObj,
        greenhouse: greenhouse?.id || orgObj.greenhouse,
        plot: plot.id,
        date: nextDate || orgObj.date,
      },
    });
  };

  const fetchGreenhouses = async (orgId, siteId, cropId = null) => {
    setGreenhouseLoading(true);
    const res = await getGreenhouses(orgId, siteId, cropId);
    const greenhouseList = Array.isArray(res?.data?.data)
      ? res.data.data
      : [];
    const data = greenhouseList.map((greenhouse) => {
      return {
        name: greenhouse.name,
        id: greenhouse.id,
        plots: (greenhouse.plots || []).map((plot) => ({
          ...plot,
          greenhouse_id: greenhouse.id,
          greenhouse_name: greenhouse.name,
        })),
      };
    });
    setGreenhouseData(data);
    setGreenhouseLoading(false);
  };

  const disabledDate = (current) => {
    const dayjsDates = dateData.map((date) => dayjs(date));
    return (
      current &&
      !dayjsDates.reduce((isEnabled, date) => {
        if (isEnabled) {
          return true;
        } else {
          return date.isSame(current, "day");
        }
      }, false)
    );
  };

  const handleDateChange = (date, dateString) => {
    if (dateString) {
      setCurrentDate(dateString);
      const params = new URLSearchParams(searchParams);
      params.set("date", dateString);
      setSearchParams(params);
    }
  };

  // Fetch greenhouses (and set greenhouse label/block label) on site/crop change
  useEffect(() => {
    if (currentSite && currentOrg) {
      const siteMetaData = currentSite.metadata[currentCrop?.name] || {};
      setSiteMetadata(siteMetaData);
      if (siteMetaData?.greenhouse_name) {
        setGreenHouseLabel(siteMetaData.greenhouse_name);
      }
      if (siteMetaData?.block_name) {
        setBlockLabel(siteMetaData.block_name);
      }
      fetchGreenhouses(currentOrg, currentSite.id, currentCrop?.id);
    }
  }, [currentSite, currentCrop, currentOrg]);

  useEffect(() => {
    if (greenhouseData && greenhouseData.length) {
      let params = new URLSearchParams(searchParams);
      let greenhouseIdFromUrlParams = params.get("greenhouse");

      if (greenhouseIdFromUrlParams) {
        console.log("SETTING FROM URL PARAMS");
        const greenhouseFromUrl = greenhouseData.find(
          (item) => item.id === greenhouseIdFromUrlParams,
        );
        if (greenhouseFromUrl) {
          setCurrentGreenhouse(greenhouseFromUrl);
          setPlotData(greenhouseFromUrl.plots);
        } else {
          console.log("SETTING FIRST Greenhouse OF DATA");
          setCurrentGreenhouse(greenhouseData[0]);
          setPlotData(greenhouseData[0].plots);
        }
      } else if (!settingDefaults.greenhouse) {
        const defaultGHId =
          defaultDropdown && defaultDropdown[currentOrg]?.greenhouse;

        if (defaultGHId) {
          const matchingGreenhouse = greenhouseData.find(
            (gh) => gh.id === defaultGHId,
          );
          if (matchingGreenhouse) {
            setCurrentGreenhouse(matchingGreenhouse);
            setPlotData(matchingGreenhouse.plots);
          } else {
            setCurrentGreenhouse(greenhouseData[0]);
            setPlotData(greenhouseData[0].plots);
          }
        } else {
          // fallback
          setCurrentGreenhouse(greenhouseData[0]);
          setPlotData(greenhouseData[0].plots);
        }
        setSettingDefaults({ ...settingDefaults, greenhouse: true });
      } else {
        console.log("SETTING FIRST greenhouse OF DATA");
        setCurrentGreenhouse(greenhouseData[0]);
        setPlotData(greenhouseData[0].plots);
      }
    } else {
      setCurrentGreenhouse(null);
      setPlotData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [greenhouseData]);

  // Whenever current greenhouse changes, update plotData
  useEffect(() => {
    if (currentSite && currentOrg && currentCrop && currentGreenhouse) {
      setPlotData(currentGreenhouse.plots);
    }
  }, [currentGreenhouse]);

  // Whenever plot changes, set the date data
  useEffect(() => {
    if (currentPlot) {
      setDateData(getPlotDates(currentPlot));
    }
  }, [currentPlot, setDateData]);

  useEffect(() => {
    if (plotData && plotData.length) {
      let params = new URLSearchParams(searchParams);
      let plotIdFromUrlParams = params.get("plot");
      let dateFromUrlParams = params.get("date");
      if (plotIdFromUrlParams) {
        console.log("SETTING FROM URL PARAMS");
        const plotFromUrl = findPlot(plotIdFromUrlParams);
        if (plotFromUrl) {
          syncPlotSelection(plotFromUrl, currentGreenhouse, dateFromUrlParams);
        } else {
          console.log("SETTING FIRST PLOT OF DATA");
          syncPlotSelection(plotData[0], currentGreenhouse, dateFromUrlParams);
        }
      } else if (!settingDefaults.plot) {
        console.log("SETTING DEFAULT plot from localStorage");
        let defaultPlotId =
          defaultDropdown && defaultDropdown[currentOrg]?.plot;
        let defaultDate = defaultDropdown && defaultDropdown[currentOrg]?.date;

        if (defaultPlotId) {
          let defaultPlotValue = findPlot(defaultPlotId);
          if (defaultPlotValue) {
            syncPlotSelection(defaultPlotValue, currentGreenhouse, defaultDate);
          } else {
            // fallback
            syncPlotSelection(plotData[0], currentGreenhouse, defaultDate);
          }
        } else {
          syncPlotSelection(plotData[0], currentGreenhouse, defaultDate);
        }
        setSettingDefaults({ ...settingDefaults, plot: true });
      } else {
        console.log("SETTING FIRST PLOT OF DATA");
        syncPlotSelection(plotData[0], currentGreenhouse);
      }
    } else {
      setCurrentPlot(null);
      setDateData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plotData]);

  useEffect(() => {
    let params = new URLSearchParams(searchParams);
    let dateFromUrl = params.get("date");
    if (dateFromUrl) {
      if (dateData && dateData.includes(dateFromUrl)) {
        setCurrentDate(dateFromUrl);
      } else {
        const dateString = dateData ? dateData[0] : null;
        setCurrentDate(dateString);
      }
    } else {
      const dateString = dateData ? dateData[0] : null;
      setCurrentDate(dateString);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateData]);

  // Whenever current greenhouse changes, set it in query params + localStorage
  useEffect(() => {
    if (currentGreenhouse) {
      let orgObj = defaultDropdown ? defaultDropdown[currentOrg] : {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          greenhouse: currentGreenhouse.id, // store ID only!
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGreenhouse]);

  // Whenever current plot changes, set it in query params + localStorage
  useEffect(() => {
    if (currentPlot) {
      let orgObj = defaultDropdown ? defaultDropdown[currentOrg] : {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          plot: currentPlot.id,
        },
      });
    }
  }, [currentPlot]);

  // Set loading state for dropdowns
  useEffect(() => {
    setDropdownLoading(
      orgLoading ||
        siteLoading ||
        cropLoading ||
        plotLoading ||
        greenhouseLoading ||
        !currentDate,
    );
  }, [
    orgLoading,
    cropLoading,
    siteLoading,
    plotLoading,
    greenhouseLoading,
    currentDate,
    setDropdownLoading,
  ]);

  return (
    <>
      {orgLoading || siteLoading || plotLoading || greenhouseLoading ? (
        <LoadingIcon height="100px" />
      ) : (
        <div className="w-full flex flex-wrap gap-4 justify-between sm:flex-col mb-10 dropdowns sm:mb-4">
          {userData?.is_superuser && (
            <TopDropdown
              label="Select Org:"
              val={currentOrg ?? undefined}
              classString="flex-1 min-w-[150px] sm:mb-2"
              placeholder="Select Organization"
              changeCallback={setCurrentOrg}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              filterSort={(a, b) =>
                (a?.label ?? "")
                  .toLowerCase()
                  .localeCompare((b?.label ?? "").toLowerCase())
              }
              notFound={orgLoading ? <LoadingIcon /> : null}
              data={orgData.map((org) => ({
                value: org.id,
                label: org.name,
              }))}
            />
          )}

          {/* Site dropdown => ID-based */}
          <TopDropdown
            label="Select Site:"
            val={currentSite?.id ?? undefined}
            classString="flex-1 min-w-[150px] sm:mb-2"
            placeholder="Select Site"
            changeCallback={(siteId) => {
              const siteObj = siteData.find((s) => s.id === siteId);
              setCurrentSite(siteObj);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(a, b) =>
              (a?.label ?? "")
                .toLowerCase()
                .localeCompare((b?.label ?? "").toLowerCase())
            }
            notFound={siteLoading ? <LoadingIcon /> : null}
            data={siteData?.map((site) => ({
              value: site.id,
              label: site.displayName,
            }))}
          />

          {/* Crop dropdown => ID-based */}
          <TopDropdown
            label="Select Crop:"
            val={currentCrop?.id ?? undefined}
            placeholder="Select Crop"
            classString="flex-1 min-w-[150px] sm:mb-2"
            changeCallback={(cropId) => {
              const cropObj = cropData.find((c) => c.id === cropId);
              setCurrentCrop(cropObj);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(a, b) =>
              (a?.label ?? "")
                .toLowerCase()
                .localeCompare((b?.label ?? "").toLowerCase())
            }
            data={cropData?.map((crop) => ({
              value: crop.id,
              label: crop.displayName,
            }))}
          />
          <TopDropdown
            label={`Select ${greenHouseLabel}:`}
            val={currentGreenhouse?.id ?? undefined}
            placeholder={`Select ${greenHouseLabel}`}
            classString="flex-1 min-w-[150px] sm:mb-2"
            changeCallback={(id) => {
              const greenhouseObj = greenhouseData.find((gh) => gh.id === id);
              setCurrentGreenhouse(greenhouseObj);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            data={(() => {
              if (!greenhouseData) return [];
              const allNamesAreNumeric = greenhouseData.every((gh) =>
                /^\d+$/.test(gh.name),
              );

              const sortedGreenhouseData = [...greenhouseData];
              if (allNamesAreNumeric) {
                sortedGreenhouseData.sort(
                  (a, b) => parseInt(a.name) - parseInt(b.name),
                );
              } else {
                sortedGreenhouseData.sort((a, b) =>
                  a.name.localeCompare(b.name),
                );
              }

              return sortedGreenhouseData.map((greenhouse) => ({
                label: greenhouse.name,
                value: greenhouse.id,
              }));
            })()}
          />
          <TopDropdown
            label={`Select ${blockLabel}:`}
            val={
              currentGreenhouse && currentPlot
                ? `${currentGreenhouse.id}---${currentPlot.id}`
                : undefined
            }
            placeholder={`Select ${blockLabel}`}
            classString="flex-1 min-w-[150px] sm:mb-2"
            changeCallback={(value) => {
              const [greenhouseId, plotId] = value.split("---");
              const greenhouse = greenhouseData.find((gh) => gh.id === greenhouseId);
              syncPlotSelection(findPlot(plotId, greenhouse), greenhouse);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            data={(() => {
              if (!plotData) return [];
              const allNamesAreNumeric = plotData.every((plot) =>
                /^\d+$/.test(plot.name),
              );

              const sortedPlotData = [...plotData];
              if (allNamesAreNumeric) {
                sortedPlotData.sort(
                  (a, b) => parseInt(a.name) - parseInt(b.name),
                );
              } else {
                sortedPlotData.sort((a, b) => a.name.localeCompare(b.name));
              }

              return sortedPlotData.map((plot) => ({
                label: plot.name,
                value: `${plot.greenhouse_id || currentGreenhouse?.id}---${plot.id}`,
              }));
            })()}
          />
          {/*<div className="flex flex-col">*/}
          {/*  <div className="text-sm text-darkerGray max-w-full mb-1">*/}
          {/*    Select Date:*/}
          {/*  </div>*/}
          {/*  <DatePicker*/}
          {/*    value={currentDate ? dayjs(currentDate) : null}*/}
          {/*    className="flex-1 min-w-[150px] sm:mb-2"*/}
          {/*    disabledDate={disabledDate}*/}
          {/*    onChange={handleDateChange}*/}
          {/*  />*/}
          {/*</div>*/}
        </div>
      )}
    </>
  );
};

export default Dropdowns;
